import ApiService from "../Services/ApiService";

export const SCENARIO_NORMAL = "SCENARIO_NORMAL";
export const SCENARIO_ATTESTATION = "SCENARIO_ATTESTATION";
export const SCENARIO_APPROVAL = "SCENARIO_APPROVAL";
/** Prof et client à la même adresse : approbation admin requise (pas de preuve) */
export const SCENARIO_SAME_ADDRESS = "SCENARIO_SAME_ADDRESS";

export interface TravelAttestationInfo {
    id: number;
    distanceKm: string;
    teachrAddress: string;
    clientAddress: string;
    travelMode: string | null;
    travelModeLabel: string;
    signedAt: string | null;
    url: string;
    supportingDocs: Array<{ path: string; url: string }>;
}

export interface TravelInfo {
    distanceKm: number | null;
    scenario: string;
    source: string | null;
    thresholdLow: number;
    thresholdHigh: number;
    attestation: TravelAttestationInfo | null;
    /** true si le prof et le client sont domiciliés à la même adresse */
    sameAddress?: boolean;
    addressChangedSinceAttestation: boolean;
    /** Raison de l'invalidation de la dernière attestation (adresse modifiée, changement de mode…) */
    invalidationReason?: string | null;
    travelModes: Record<string, string>;
    /** P7 : mode de déplacement enregistré pour ce client, ou null si jamais renseigné. */
    travelMode?: TravelModeInfo | null;
    /** P7 : anomalie active à afficher au prof (bannière), ou null. */
    complianceAlert?: ComplianceAlertInfo | null;
    /** P6 : abonnement couvrant la période demandée (ou le jour même), s'il existe. */
    transportSubscription?: TransportSubscriptionSummary | null;
    /** P6 : abonnements encore utilisables pour ce client, même s'ils ne couvrent pas la période. */
    transportSubscriptions?: TransportSubscriptionSummary[];
    /**
     * Pourquoi aucun abonnement ne couvre : "none" (aucun), "other_client"
     * (abonnements rattachés à d'autres clients), "period" (hors dates),
     * "to_confirm" (adresse modifiée, confirmation du prof attendue).
     * Null quand un abonnement couvre.
     */
    transportSubscriptionReason?: "none" | "other_client" | "period" | "to_confirm" | null;
    /** Le mode enregistré est « Transports en commun » ou « Train » : un abonnement a un sens. */
    travelModeUsesSubscription?: boolean;
    /** Plus de 160 km : une preuve (abonnement ou billet) est exigée à chaque déclaration. */
    subscriptionRequired?: boolean;
}

/** Modes pour lesquels l'abonnement de transport est proposé (même règle que TravelModeEnum côté serveur). */
export const SUBSCRIPTION_TRAVEL_MODES = ["public_transport", "train"];

/** Jamais d'abonnement pour la voiture, les deux-roues, la marche ou le vélo. */
export const modeUsesSubscription = (mode?: string | null): boolean =>
    !!mode && SUBSCRIPTION_TRAVEL_MODES.includes(mode);

/** P7 : anomalie de conformité (mode de déplacement incohérent avec la distance), jamais bloquante. */
export interface ComplianceAlertInfo {
    id: number;
    triggerType: string;
    status: "open" | "confirmed_by_user" | "resolved" | "dismissed";
    travelMode: string | null;
    travelModeLabel: string;
    distanceKm: number | null;
    thresholdKm: number | null;
    courseId: number | null;
    /** « Distance inhabituelle détectée (environ 12 km à pied), confirmez-vous ce déplacement ? » */
    message: string;
    createdAt: string | null;
    confirmedAt: string | null;
}

/** Vue admin d'une anomalie (onglet « Anomalies »). */
export interface ComplianceFlagAdmin extends ComplianceAlertInfo {
    teachr: { id: number; firstName: string; lastName: string; mail: string } | null;
    client: { id: number; firstName: string; lastName: string } | null;
    resolvedAt: string | null;
    resolutionNote: string | null;
    resolvedBy: string | null;
}

/** Mode de déplacement de référence du couple prof/client, connu même sans attestation. */
export interface TravelModeInfo {
    mode: string;
    label: string;
    distanceKm: number | null;
    since: string | null;
}

export interface TransportSubscriptionSummary {
    id: number;
    type: string;
    typeLabel: string;
    validFrom: string;
    validUntil: string;
    /** Client du couple ; null pour un abonnement « tous clients » (ancien dépôt). */
    clientId?: number | null;
    clientName?: string | null;
    /** À confirmer après un changement d'adresse : ne vaut pas preuve tant que le prof n'a pas confirmé. */
    needsConfirmation?: boolean;
    confirmationReason?: string | null;
}

/**
 * Gestion des déplacements prof/client : distance, scénario de déclaration,
 * attestation sur l'honneur (60–160 km), preuve de déplacement (> 160 km)
 * et workflow de revue admin.
 */
export default abstract class TravelRepository {
    /**
     * addressId = adresse du cours choisie dans le flux : le scénario (distance,
     * adresse identique) est calculé sur elle plutôt que sur l'adresse par
     * défaut du client, même décision que le backend à la création du cours.
     * P6 : la période du cours (YYYY-MM-DD) permet au backend de renvoyer
     * l'abonnement de transport qui la couvre, et non celui du jour même.
     */
    public static getTravelInfo(
        teachrId: number,
        clientId: number,
        addressId?: number | null,
        period?: { startDate?: string; endDate?: string }
    ): Promise<TravelInfo> {
        const params: Record<string, string | number> = {};
        if (addressId != null) {
            params.addressId = addressId;
        }
        if (period?.startDate && period?.endDate) {
            params.startDate = period.startDate;
            params.endDate = period.endDate;
        }
        return ApiService.get(`/teachrs/${teachrId}/clients/${clientId}/travel-info`, params);
    }

    public static signAttestation(teachrId: number, clientId: number, travelMode: string): Promise<TravelAttestationInfo> {
        return ApiService.post(`/teachrs/${teachrId}/clients/${clientId}/travel-attestation/sign`, { travelMode }) as Promise<TravelAttestationInfo>;
    }

    /**
     * P7 : mode de déplacement de référence, hors attestation (fiche client,
     * question à la première déclaration). Renvoie l'éventuelle anomalie relevée.
     */
    public static setTravelMode(
        teachrId: number,
        clientId: number,
        travelMode: string
    ): Promise<{ travelMode: TravelModeInfo; complianceAlert: ComplianceAlertInfo | null; attestationInvalidated: boolean }> {
        return ApiService.put(`/teachrs/${teachrId}/clients/${clientId}/travel-mode`, { travelMode } as any) as Promise<{
            travelMode: TravelModeInfo;
            complianceAlert: ComplianceAlertInfo | null;
            attestationInvalidated: boolean;
        }>;
    }

    /** P7 : le prof confirme son déplacement, la bannière disparaît (le flag reste visible de l'admin). */
    public static confirmComplianceFlag(flagId: number): Promise<ComplianceAlertInfo> {
        return ApiService.post(`/compliance-flags/${flagId}/confirm`, {}) as Promise<ComplianceAlertInfo>;
    }

    public static addAttestationDocument(attestationId: number, file: File): Promise<{ path: string; url: string }> {
        return ApiService.postInstance(`/travel-attestations/${attestationId}/documents`, null, file, false, true);
    }

    public static uploadTravelProof(courseId: number, file: File): Promise<{ path: string; url: string; declarationStatus: string }> {
        return ApiService.postInstance(`/punctual-courses/${courseId}/travel-proof`, null, file, false, true);
    }

    // ── Admin ──────────────────────────────────────────────────────────────

    public static getDeclarationReviews(): Promise<{ toApprove: any[]; toVerify: any[]; counts: { toApprove: number; toVerify: number } }> {
        return ApiService.get(`/admin/declaration-reviews`);
    }

    public static approveCourse(courseId: number): Promise<any> {
        return ApiService.patch(`/courses/${courseId}/approve`);
    }

    public static rejectCourse(courseId: number, reason?: string): Promise<any> {
        return ApiService.patch(`/courses/${courseId}/reject`, reason ? { reason } : {});
    }

    /** Refus définitif : le cours est annulé (is_cancelled) et ne sera pas transmis à l'URSSAF. */
    public static refuseCourse(courseId: number, reason?: string): Promise<any> {
        return ApiService.patch(`/courses/${courseId}/refuse`, reason ? { reason } : {});
    }

    public static verifyCourse(courseId: number): Promise<any> {
        return ApiService.patch(`/courses/${courseId}/verify`);
    }

    // ── Admin : anomalies P7 ─────────────────────────────────────────────────

    public static getComplianceFlags(status: "active" | "closed" | "all" = "active"): Promise<{
        flags: ComplianceFlagAdmin[];
        counts: { active: number; open: number; confirmedByUser: number; resolved: number; dismissed: number };
    }> {
        return ApiService.get(`/admin/compliance-flags?status=${status}`);
    }

    public static resolveComplianceFlag(flagId: number, note?: string): Promise<ComplianceFlagAdmin> {
        return ApiService.patch(`/admin/compliance-flags/${flagId}`, { action: "resolve", note: note || null }) as Promise<ComplianceFlagAdmin>;
    }

    public static dismissComplianceFlag(flagId: number, note?: string): Promise<ComplianceFlagAdmin> {
        return ApiService.patch(`/admin/compliance-flags/${flagId}`, { action: "dismiss", note: note || null }) as Promise<ComplianceFlagAdmin>;
    }
}
